/**
 * Test Fixtures: Mock API responses and Modbus data
 */

// Auth data for role 1 (regular user)
export const mockAuthRole1 = {
    token: 'test-token-123',
    username: 'testuser',
    role: 1,
    expires: new Date(Date.now() + 86400000).toISOString(),
    profile: { firstName: 'Test', lastName: 'User' },
    devices: [{
        deviceId: 'FM353691848531173',
        vehicleId: 'TEST-EV-001',
        _id: 'dev123',
        carProfile: { model: '2DEYE2GCE9M' }
    }]
};

// Auth data for role 2 (admin)
export const mockAuthRole2 = {
    token: 'admin-token-456',
    username: 'admin',
    role: 2,
    expires: new Date(Date.now() + 86400000).toISOString(),
    profile: { firstName: 'Admin', lastName: 'User' }
};

// Real Modbus request→response pairs (from actual device data)
export const mockModbusData = {
    // SlaveId=4, StartAddress=0x1100, 32 registers (RBMS single battery)
    '040311000020417B': '040340170c0033aaaa01ff00000111000000640064001f000100030018000900080cd1000100050cca0009000b00000000039f01ff000001ff000001ff0000000000009292',
    // SlaveId=3, StartAddress=0x1100, 32 registers
    '03031100002040CC': '03034017660033aaaa01ff00000111000000640064001f000100020019000600050d040001000e0cfd0003000100000000079f01ff000001ff000001ff000000000000483d',
    // SlaveId=4, StartAddress=0x2000, 32 registers (cell voltages + temps)
    '0403200000204F87': '0403400cd00cd10cd00cd00cd10cd00cd10cd10cd00cd10cce0cd00cd00cd00cd00cd10cd10cd00cd00ccf0000000000000000001e001e001f001e001e001d001e001d3bf5',
};

// Mock modbus data with active faults (alarm bit 0 = Total Voltage High)
export const mockModbusDataWithFaults = {
    '040311000020417B': '040340170c0033aaaa01ff00000111000000640064001f000100030018000900080cd1000100050cca00090001000100000001000100000001000000010000000000009292',
};

// Mock getDeviceStatus API response
export const mockDeviceStatusResponse = {
    status: 'success',
    vstatData: {
        deviceId: 'FM353691848531173',
        vstatObj: {
            fm: {
                latitude: 13.2072816,
                longitude: 77.9888166,
                altitude: 926,
                speed: 0,
                direction: 0,
                numSatellites: 13,
                deviceTimeStamp: '2026-02-15T12:14:06.000Z',
                ioElements: {
                    rs485modbus: { ...mockModbusData }
                }
            },
            localTimeStamp: '2026-02-15T12:14:06.689Z',
            deviceId: 'FM353691848531173'
        }
    }
};

// Mock getLocationInterval API response
export const mockLocationIntervalResponse = {
    locationInfo: [
        {
            localTimeStamp: '2026-02-15T10:00:00.000Z',
            params: {
                rs485modbus: { ...mockModbusData }
            }
        },
        {
            localTimeStamp: '2026-02-15T10:05:00.000Z',
            params: {
                rs485modbus: { ...mockModbusData }
            }
        }
    ]
};

// Mock groups API response
export const mockGroupsResponse = {
    groups: [
        { _id: 'group1', groupName: 'Test Group A' },
        { _id: 'group2', groupName: 'Test Group B' }
    ]
};

// Mock vehicles API response
export const mockVehiclesResponse = {
    vehicles: [
        {
            _id: 'v1',
            deviceId: 'FM353691848531173',
            vehicleId: 'TEST-EV-001',
            carProfile: { model: '2DEYE2GCE9M' }
        },
        {
            _id: 'v2',
            deviceId: 'FM353691848531174',
            vehicleId: 'TEST-EV-002',
            carProfile: { model: '1DEYE1GCE9M' }
        }
    ]
};

// Mock getEnergySummary API response (energy overview) — shape defined by the spec's
// getEnergySummary contract; the real endpoint is a separate backend deliverable.
export const mockEnergySummaryResponse = {
    status: 'success',
    summarys: [
        {
            veh: 'FM353691848531173',
            sst: new Date('2026-06-30T00:00:00.000Z').getTime(),
            dur: 14400, // 4 hours total backup that day, in seconds
            sessions: [
                {
                    battery_name: 'Battery 1 (Slave 3)',
                    start_time: '2026-06-30T02:00:00.000Z',
                    end_time: '2026-06-30T04:00:00.000Z',
                    power_provided: 3000, // Wh
                    backup_time: 7200 // seconds
                },
                {
                    battery_name: 'Battery 2 (Slave 4)',
                    start_time: '2026-06-30T10:00:00.000Z',
                    end_time: '2026-06-30T12:00:00.000Z',
                    power_provided: 2500,
                    backup_time: 7200
                }
            ]
        },
        {
            veh: 'FM353691848531173',
            sst: new Date('2026-07-01T00:00:00.000Z').getTime(),
            dur: 3600,
            sessions: [
                {
                    battery_name: 'Battery 1 (Slave 3)',
                    start_time: '2026-07-01T06:00:00.000Z',
                    end_time: '2026-07-01T07:00:00.000Z',
                    power_provided: 1800,
                    backup_time: 3600
                }
            ]
        }
    ]
};

// DALY Live Modbus response fixture
export const mockDalyLiveModbus = {
    '$123,869356078398480,aquila123,debug,153:81030000007F1BEA*':
        '5103FE0CE00CE20CE20CE20CE10CE10CE10CE30CE20CE10CE20CE10CE20CE10CE10CE100000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000004D004D004D004D0000000000000000020F753003E8000F001000040CE300080CE000010003004D0004004D0001000000000001000003E800010001AE700000000000000001000100000000000000000000FFFF0053005200000000FFFF000075301A08110F2402000006020000000000000002752C0001FFFF0000000000000000008000000000FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF'
};

// DALY Historic Modbus fixture (with prepended request echo)
export const mockDalyHistoricModbus = {
    '$$123,869356078398480,aquila123,debug,153:81030000007F1BEA*':
        '81030000007F1BEA5103FE0CE00CE20CE30CE20CE10CE10CE10CE40CE20CE20CE20CE20CE20CE10CE20CE200000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000000004B004B004D004D0000000000000000020F753003E80058001000040CE400080CE100010003004D0004004B0001000200000001000003E800010001AE700000000000000001000100000000000000000000FFFF0052005000000000FFFF000075301A08110D0324000006020000000000000002752D0001FFFF0000000000000000008000000000FFFFFFFFFFFFFFFFFFFFFF'
};

// DALY Corrupt Modbus fixture
export const mockDalyCorruptModbus = {
    '$$123,869356078398480,aquila123,debug,153:81030000007F1BEA*':
        '3F7EFFFF01C92B00577E80DE383B5E383BDE383D5E3837DE383BDE383BDE383BDE383DFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF67FF69FF65FF65FFFFFFFFFFFFFFFFFBE1159FF92F87FFDFFFF7E737EFE73DFDFFF9FF65FFF7FF69FFFBFFFBFFFFFFFDFFFFF92FFDFFFDA3F4FFFFFFFFFFFFFDFFFDFFFFFFFFFFFFFFFFFFFF01005BFF5FFFFFFFFF0100FF159FCBEFDDE5F1CFFFFFF3FBFFFFFFFFFFFFFFFB15A7FFF50100FFFFFFFFFFFFFFFFFFFFFFFF01000000000000000000000000000000000000000000A821'
};

// Mock DALY getDeviceStatus API response
export const mockDalyDeviceStatusResponse = {
    status: 'success',
    vstatData: {
        deviceId: 'AIS869356078398480',
        vstatObj: {
            ais: {
                lat: 13.0827,
                lng: 80.2707,
                deviceTimeStamp: '2026-08-17T10:06:02.000Z',
                rs485modbus: { ...mockDalyLiveModbus }
            },
            localTimeStamp: '2026-08-17T10:06:02.000Z',
            deviceId: 'AIS869356078398480'
        }
    }
};

// Mock DALY getLocationInterval API response
export const mockDalyLocationIntervalResponse = {
    locationInfo: [
        {
            localTimeStamp: '2026-08-17T10:00:00.000Z',
            rs485modbus: { ...mockDalyHistoricModbus }
        },
        {
            localTimeStamp: '2026-08-17T10:05:00.000Z',
            rs485modbus: {}
        },
        {
            localTimeStamp: '2026-08-17T10:10:00.000Z',
            rs485modbus: { ...mockDalyCorruptModbus }
        }
    ]
};

// Mock DALY vehicle profile
export const mockDalyVehicle = {
    deviceId: 'AIS869356078398480',
    vehicleId: 'TERRANOVA TEST',
    carProfile: { model: 'DALYLV' }
};

// Mock getUserInfoForApp API responses (§3.2 real payload)
export const mockUserInfoForAppResponse = {
    status: 'success',
    userInfo: {
        role: 0,
        group: '6a7446094b041509a5bd4fef',
        vehicleInfo: [{
            _id: '6a7ee3a059669316f3138458',
            device: '6a75905ee40f2e0b8eee596c',
            deviceId: 'AIS869356078398480',
            VehicleProfile: {
                VehicleName: 'TERRANOVA TEST',
                Make: 'Terranova',
                Model: 'DALYLV',
                RegNo: 'TERRANOVA TEST',
                Year: 2026
            }
        }],
        deviceInfo: []
    }
};

// Auth data for role 0
export const mockAuthRole0 = {
    token: 'role0-token',
    username: '7816016662',
    role: 0,
    expires: new Date(Date.now() + 86400000).toISOString(),
    devices: [],
    profile: { firstName: 'Role0', lastName: 'User' }
};

// Empty vehicleInfo response
export const mockUserInfoForAppEmpty = {
    status: 'success',
    userInfo: {
        vehicleInfo: [],
        deviceInfo: []
    }
};

// Failed getUserInfoForApp response
export const mockUserInfoForAppFail = {
    status: 'fail',
    message: 'User Object not found.'
};

